import { Injectable } from '@angular/core';

import { RegionTally, SearchHit } from '../map/map-queries';
import { HoverInfo, MapMode, MapTool, OVERVIEW, SuburbMapEngine } from '../map/suburb-map.engine';
import { SubRegionAction } from '../rules/suburb-subregion-edits';
import { Panel, WorkspaceView } from './panel';
import { MapFilter } from './workspace-keys';

/** One saved map action the server can undo (its batch), and what the status line calls it. */
export interface UndoStep {
  batchId: string;
  label: string;
}

/** The workspace's own "are you sure" (a save that would remove suburbs from pages), answered in its dialog. */
export interface ConfirmRequest {
  message: string;
  resolve: (ok: boolean) => void;
}

/**
 * The Suburbs workspace's state (#256), in one place, for one open popup (provided by the workspace component):
 * which view and panel are showing, the map's mode, tool, active region and sub-region and filters, the undo stack,
 * the status line, and the tallies. The panels, the tool column and the map read and change it here.
 *
 * The map's tool settings (mode, tool, active region and sub-region, filters) live in the engine, which applies them
 * as it draws and edits; the accessors here are the one way in, and redraw the map as the settings change.
 *
 * Redrawing the popup: the app coalesces change detection into animation frames (main.ts, for iPad tap latency), and
 * work that finishes outside Angular's zone, keyboard shortcuts and the map's own events don't reliably get a pass of
 * their own here; so the services call redraw(), which the workspace component binds to its change detector.
 */
@Injectable()
export class WorkspaceStateService {
  /** The map, once the workspace has attached it (MapHostService.attach). */
  engine!: SuburbMapEngine;
  view: WorkspaceView = 'map';
  panel: Panel | null = null;
  stateCode = 'nsw';
  loading = true;
  loadError = '';
  /** Editing switched off (nothing sets it today: the touch-screen rule was dropped, Ivan 2026-10-16). */
  readOnly = false;
  status = 'Loading…';
  statusError = false;
  /** Saves queued or in flight. */
  saving = 0;
  undoStack: UndoStep[] = [];
  hover: HoverInfo | null = null;
  confirm: ConfirmRequest | null = null;
  /** Assigned and covered suburbs per region, members per sub-region, and how many of the loaded state are assigned. */
  counts = new Map<string, RegionTally>();
  memberCounts = new Map<string, number>();
  here = { assigned: 0, total: 0 };
  searchText = '';
  results: SearchHit[] = [];

  private redrawView: (() => void) | null = null;

  /** The workspace component's change detection, for redraw(). */
  bindRedraw(redraw: () => void): void {
    this.redrawView = redraw;
  }

  unbindRedraw(): void {
    this.redrawView = null;
  }

  /** Redraw the popup now (see the class comment). A no-op once the popup is gone. */
  redraw(): void {
    this.redrawView?.();
  }

  setStatus(message: string, error = false): void {
    this.status = message;
    this.statusError = error;
  }

  /** Ask in the workspace's own dialog; resolves when it's answered. */
  ask(message: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.confirm = { message, resolve };
      this.redraw();
    });
  }

  answer(ok: boolean): void {
    const c = this.confirm;
    this.confirm = null;
    c?.resolve(ok);
  }

  /** The assignments changed: recount, and rerun the search so its rows say where each suburb stands now. */
  mapChanged(): void {
    if (!this.engine) return;
    this.counts = this.engine.regionCounts();
    this.memberCounts = this.engine.subRegionCounts();
    this.here = this.engine.inLoadedState();
    if (this.searchText) this.results = this.engine.search(this.searchText);
  }

  search(): void {
    this.results = this.engine.search(this.searchText);
  }

  /** The all-regions review is showing (or loading): read-only. */
  get overview(): boolean {
    return this.stateCode === OVERVIEW;
  }

  /** The suburb whose panel is open. */
  get selectedCode(): string | null {
    return this.panel?.kind === 'suburb' ? this.panel.salCode : null;
  }

  /** The selected suburb's live state on the map (the engine is the source of truth while editing). */
  get selectedArea(): { regionId: string | null; covered: boolean; subRegions: string[] } {
    const a = this.selectedCode ? this.engine.areas.get(this.selectedCode) : undefined;
    return {
      regionId: a?.regionId ?? null,
      covered: !!a?.covered,
      subRegions: a?.subRegions ?? [],
    };
  }

  // ------------------------------------------------------------------ the map's tool settings (held by the engine)

  get mode(): MapMode {
    return this.engine?.mode ?? 'view';
  }
  set mode(m: MapMode) {
    this.engine.mode = m;
    this.engine.syncToolCursor();
    this.engine.render(); // the active sub-region's fill shows in Sub-region mode only
  }

  get tool(): MapTool {
    return this.engine?.tool ?? 'pointer';
  }
  set tool(t: MapTool) {
    this.engine.tool = t;
    this.engine.syncToolCursor();
  }

  get activeRegion(): string | null {
    return this.engine?.activeRegion ?? null;
  }
  set activeRegion(id: string | null) {
    this.engine.activeRegion = id; // resets Assign mode's sub-region to None
    this.engine.render();
  }

  get activeSubRegion(): string | null {
    return this.engine?.activeSubRegion ?? null;
  }
  set activeSubRegion(id: string | null) {
    this.engine.activeSubRegion = id;
    this.engine.render();
  }

  /**
   * Assign mode's Sub-region picker (#260, Ivan 2026-10-21): '' = None (region only), else one of the active region's
   * sub-regions that assigned suburbs join too. The engine resets it when the active region changes; it lives only as
   * long as the tool is open.
   */
  get assignSubRegion(): string {
    return this.engine?.assignSubRegionRef?.id ?? '';
  }
  set assignSubRegion(id: string) {
    this.engine.assignSubRegion = id || null;
    this.engine.render(); // the picked sub-region's outline is drawn thicker, with a light fill
  }

  get subRegionAction(): SubRegionAction {
    return this.engine?.subRegionAction ?? 'add';
  }
  set subRegionAction(a: SubRegionAction) {
    this.engine.subRegionAction = a;
  }

  isFilterOn(filter: MapFilter): boolean {
    if (filter === 'labels') return this.engine?.showLabels ?? false;
    if (filter === 'subregions') return this.engine?.showSubRegions ?? false;
    return this.engine?.unassignedOnly ?? false;
  }

  setFilter(filter: MapFilter, on: boolean): void {
    if (filter === 'labels') this.engine.showLabels = on;
    else if (filter === 'subregions') this.engine.showSubRegions = on;
    else this.engine.unassignedOnly = on;
    this.engine.render();
    this.mapChanged();
  }

  isRegionShown(id: string): boolean {
    return !this.engine?.hiddenRegions?.has(id);
  }

  showRegion(id: string, shown: boolean): void {
    if (shown) this.engine.hiddenRegions.delete(id);
    else this.engine.hiddenRegions.add(id);
    this.engine.render();
  }
}
